import type { SupabaseClient } from '@supabase/supabase-js';

export interface OwnerNotePage {
  readonly org_id: string;
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface NoteCounts {
  readonly lines: number;
  readonly words: number;
  readonly letters: number;
}

export const NOTE_AUTOSAVE_MS = 700;

export function noteCounts(text: string): NoteCounts {
  if (text.length === 0) return { lines: 0, words: 0, letters: 0 };
  return {
    lines: text.split('\n').length,
    words: text.match(/\S+/gu)?.length ?? 0,
    letters: text.match(/\p{L}/gu)?.length ?? 0,
  };
}

export function noteLineNumbers(text: string): string {
  const lines = noteCounts(text).lines;
  return lines === 0 ? '' : Array.from({ length: lines }, (_, index) => String(index + 1)).join('\n');
}

export function normalizedNoteTitle(title: string): string {
  const trimmed = title.trim();
  return trimmed === '' ? 'Untitled page' : trimmed.slice(0, 120);
}

export function sortOwnerNotePages(pages: readonly OwnerNotePage[]): OwnerNotePage[] {
  return [...pages].sort((left, right) => {
    const byUpdated = (Date.parse(right.updated_at) || 0) - (Date.parse(left.updated_at) || 0);
    if (byUpdated !== 0) return byUpdated;
    const byCreated = (Date.parse(right.created_at) || 0) - (Date.parse(left.created_at) || 0);
    if (byCreated !== 0) return byCreated;
    return left.title.localeCompare(right.title);
  });
}

export function scheduleOwnerNoteAutosave(
  action: () => void,
  delayMs = NOTE_AUTOSAVE_MS,
): () => void {
  const timer = setTimeout(action, delayMs);
  return () => clearTimeout(timer);
}

function rpcResult<T>(data: unknown, error: { message: string } | null): T {
  const result = data as { ok?: boolean; value?: T; error?: { code?: string } } | null;
  if (error || !result?.ok || result.value === undefined) {
    throw new Error(error?.message ?? result?.error?.code ?? 'note_request_failed');
  }
  return result.value;
}

export async function loadOwnerNotePages(
  client: SupabaseClient,
  orgId: string,
): Promise<OwnerNotePage[]> {
  const { data, error } = await client.schema('brunel')
    .from('owner_note_pages')
    .select('org_id,id,title,body,created_at,updated_at')
    .eq('org_id', orgId)
    .is('archived_at', null)
    .order('updated_at', { ascending: false });
  if (error) throw new Error(error.message);
  return sortOwnerNotePages((data ?? []) as OwnerNotePage[]);
}

export async function createOwnerNotePage(
  client: SupabaseClient,
  orgId: string,
  title = 'Untitled page',
): Promise<OwnerNotePage> {
  const { data, error } = await client.rpc('create_owner_note_page', {
    request: { orgId, title: normalizedNoteTitle(title) },
  });
  return rpcResult<OwnerNotePage>(data, error);
}

export async function updateOwnerNotePage(
  client: SupabaseClient,
  orgId: string,
  page: Pick<OwnerNotePage, 'id' | 'title' | 'body'>,
): Promise<{ readonly id: string; readonly title: string; readonly updated_at: string }> {
  const { data, error } = await client.rpc('update_owner_note_page', {
    request: {
      orgId,
      pageId: page.id,
      title: normalizedNoteTitle(page.title),
      body: page.body,
    },
  });
  return rpcResult(data, error);
}

export async function archiveOwnerNotePage(
  client: SupabaseClient,
  orgId: string,
  pageId: string,
): Promise<{ readonly id: string; readonly archived_at: string }> {
  const { data, error } = await client.rpc('archive_owner_note_page', {
    request: { orgId, pageId },
  });
  return rpcResult(data, error);
}
